import { useState, useRef, useEffect, useCallback } from 'react';

const useWebcamMonitor = (constraints = { video: true }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const isInitialized = useRef(false);

  const startStream = useCallback(async () => {
    // Prevent multiple initializations
    if (isInitialized.current) return;
    
    try {
      setError(null);
      
      // Only stop existing stream if we're reinitializing
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Wait for the video to be loaded before playing
        await new Promise((resolve) => {
          if (videoRef.current.readyState >= 2) {
            resolve();
          } else {
            videoRef.current.onloadeddata = resolve;
          }
        });
        try {
          await videoRef.current.play();
        } catch (err) {
          // Intentionally ignore play() errors (autoplay restrictions, etc.)
        }
        setIsStreaming(true);
        isInitialized.current = true;
      }
    } catch (err) {
      setError(err);
      setIsStreaming(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      isInitialized.current = false;
    }
  }, [constraints]);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsStreaming(false);
    isInitialized.current = false;
  }, []);

  useEffect(() => {
    startStream();
    return () => {
      stopStream();
    };
  }, []); // Empty dependency array to ensure single initialization

  return {
    videoRef,
    isStreaming,
    error,
    startStream,
    stopStream
  };
};

export default useWebcamMonitor; 